import { z } from 'zod';
import { positiveIntegerSchema } from '../../validation';
import { objectIdSchema } from '../../validation/id';

/**
 * how an order is sourced to a carrier. `Order.shipper`'s policy, set at creation
 * time. Intermediaries (brokers in the middle of a chain) do NOT mutate this \u2014
 * their downstream dispatch is governed by their own `Offer.visibility` choice,
 * constrained by the public-promotion rule (only `Order.shipper` can publish to
 * `public_load_board`).
 *
 * - `auto` (default) \u2014 sequential cascade: try own_fleet first (when the
 *   originator has drivers), then after {@link orderAllocationSchema.autoEscalateAfter}
 *   the `escalateAutoCascade` cron job creates a `private_network` Offer.
 *   Never auto-escalates to `public_load_board` \u2014 that's a deliberate manual
 *   action by `Order.shipper`.
 * - `own_fleet` \u2014 only attempt internal driver assignment via `Order.driver`.
 *   No Offer is ever created automatically. If no driver claims, the order
 *   sits unassigned until manual intervention.
 * - `private_network` \u2014 skip own_fleet entirely. Immediately create ONE Offer
 *   with `visibility='private_network'` targeting the originator's network.
 * - `public_network` \u2014 skip own_fleet entirely. Immediately create ONE Offer
 *   with `visibility='public_load_board'`. Only valid when the creator IS
 *   `Order.shipper` (server-enforced; brokers cannot use this).
 */
export const orderAllocationStrategySchema = z.enum([
  'auto',
  'own_fleet',
  'private_network',
  'public_network',
  // - `manual` — explicit no-op on create. The post-create
  //   allocation hook does NOT pick a driver, does NOT broadcast
  //   an Offer, and does NOT schedule cron escalation. The order
  //   lands unassigned and the caller is expected to assign a
  //   driver via PATCH `/order/update` (e.g. through the
  //   assign-driver wizard). Used by Reorder Again and any flow
  //   that defers driver pick to a later, human-driven step.
  //   Without this we'd either fall through to `auto` (which
  //   silently picks the first active fleet driver, often the
  //   same person as the source order) or have to broadcast an
  //   Offer the user never asked for.
  'manual',
]);
export type OrderAllocationStrategy = z.infer<typeof orderAllocationStrategySchema>;

/**
 * how long a stalled own-fleet attempt sits before {@link orderAllocationSchema.strategy}
 * = `auto` escalates to a private_network broadcast.
 *
 * Purpose-built minimal duration shape (NOT reused from payment-terms because
 * the semantics are different \u2014 this is a "wait timeout", not a "billing term").
 * Future-safe slot for if we later need finer units.
 */
export const orderAllocationDurationUnitSchema = z.enum(['minute', 'hour', 'day']);
export type OrderAllocationDurationUnit = z.infer<typeof orderAllocationDurationUnitSchema>;

export const orderAllocationDurationSchema = z.object({
  /** numeric value (positive). e.g. `2` with unit `hour` = wait 2 hours. */
  value: positiveIntegerSchema,
  /** unit of {@link orderAllocationDurationSchema.value}. */
  unit: orderAllocationDurationUnitSchema,
});
export type OrderAllocationDuration = z.infer<typeof orderAllocationDurationSchema>;

/**
 * how the order is sourced to a carrier. Lives on `Order.allocation`.
 *
 * Future-safe slots reserved (intentionally left as comments for now):
 *   - `allowlistedTargets?: relationSchema[]` \u2014 narrow private_network broadcasts.
 *   - `cooldown?: orderAllocationDurationSchema` \u2014 minimum time between escalation rounds.
 *   - `retryPolicy?: { maxRetries, backoff }` \u2014 if a broadcast round expires
 *     with no takers and we want to retry automatically.
 */
export const orderAllocationSchema = z.object({
  /** see {@link orderAllocationStrategySchema}. Defaults to `auto`. */
  strategy: orderAllocationStrategySchema.default('auto'),
  /**
   * time on own_fleet before the `escalateAutoCascade` cron job auto-creates a
   * `private_network` Offer. Only meaningful when `strategy === 'auto'`.
   *
   * If absent (and strategy='auto'), the cron job uses a server-side default
   * defined in `OrderService.allocationConfig.defaultAutoEscalateAfter`.
   */
  autoEscalateAfter: orderAllocationDurationSchema.optional(),
  /**
   * "Send to specific carrier" hint for the `private_network` strategy.
   *
   * When set, the post-create hook creates a single targeted Offer with
   * `visibility: 'targeted'` and `targets: [{ party: <Org id resolved to Party> }]`
   * instead of fanning out to the whole network. The originator picked
   * a specific carrier Org (e.g. via the `CarrierSection` "Specific
   * Carrier" option) and we deliver the load directly.
   *
   * Stays unset for the default `auto` / `own_fleet` / open
   * `private_network` paths — those still cascade through the broader
   * Offer pipeline.
   */
  targetOrgId: objectIdSchema.optional(),
  /**
   * Same idea as `targetOrgId` but pointing at a CRM `Client._id`. Used
   * when the originator wants to dispatch to one of their signed Clients
   * (carrier role) instead of an Org. The post-create hook resolves the
   * Client to its linked Party / Org before creating the Offer.
   */
  targetClientId: objectIdSchema.optional(),
});

export type OrderAllocation = z.infer<typeof orderAllocationSchema>;
